// Kullanıcılar sayfası - Kullanıcı yönetimi (Admin)
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  FiPlus, 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiEye,
  FiKey,
  FiUsers,
  FiX
} from 'react-icons/fi';
import { TiExportOutline } from 'react-icons/ti';
import { IoFilterCircleOutline } from 'react-icons/io5';
import { CgMenuGridO } from 'react-icons/cg';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import UserModal from '../../components/Users/UserModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import UserGoalModal from '../../components/Users/UserGoalModal';
import toast from 'react-hot-toast';

const Users = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  
  // Tablo filtreleme ve sıralama
  const [filters, setFilters] = useState({
    name: '',
    department: '',
    status: ''
  });
  const [tempFilters, setTempFilters] = useState({
    name: '',
    department: '',
    status: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilters, setShowFilters] = useState({
    name: false,
    department: false,
    status: false
  });
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [selectedUserForGoal, setSelectedUserForGoal] = useState(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  // Kullanıcı listesini al (sadece admin/manager erişir)
  const { data: usersData, isLoading } = useQuery(
    ['users', currentPage, searchTerm],
    () => userService.getUsers({
      page: currentPage,
      search: searchTerm
    }),
    { keepPreviousData: true, enabled: ['admin', 'manager'].includes(currentUser?.user_type) }
  );

  // Kullanıcı silme mutation'ı
  const deleteUserMutation = useMutation(
    userService.deleteUser,
    {
      onSuccess: (data, deletedUserId) => {
        // Kullanıcıyı listeden kaldır
        queryClient.setQueryData(['users', currentPage, searchTerm], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            users: oldData.users.filter(user => user.user_uuid !== deletedUserId)
          };
        });
        
        toast.success('Kullanıcı başarıyla silindi');
        setShowDeleteModal(false);
        setSelectedUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Kullanıcı silinemedi');
      }
    }
  );

  // Modal açma fonksiyonları
  const handleAddUser = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Satır tıklama - kullanıcı detayına git
  const handleRowClick = (user) => {
    window.location.href = `/users/${user.user_uuid}`;
  };

  // Silme onayı
  const confirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.user_uuid);
    }
  };

  // Filtreleme ve sıralama fonksiyonları
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleFilter = (filterType) => {
    setShowFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  const toggleActionMenu = (userId, event) => {
    if (showActionMenu === userId) {
      setShowActionMenu(null);
    } else {
      setShowActionMenu(userId);
      // Menü pozisyonunu hesapla
      setTimeout(() => {
        const button = event.target.closest('button');
        if (button) {
          const rect = button.getBoundingClientRect();
          const menu = document.querySelector('.action-menu-dropdown');
          if (menu) {
            menu.style.left = `${rect.right - 192}px`; // 192px = min-w-48
            menu.style.top = `${rect.bottom + 4}px`;
          }
        }
      }, 0);
    }
  };

  // Menü dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) {
        setShowActionMenu(null);
      }
      
      // Filtre menülerini kapat
      if (Object.values(showFilters).some(show => show) && !event.target.closest('.filter-menu')) {
        setShowFilters({
          name: false,
          department: false,
          status: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionMenu, showFilters]);

  // Şifre resetleme fonksiyonu
  const handlePasswordReset = (user) => {
    setSelectedUserForReset(user);
    setShowPasswordReset(true);
    setPasswordData({ password: '', confirmPassword: '' });
    setPasswordErrors({});
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Hata temizle
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.password) {
      errors.password = 'Şifre gerekli';
    } else if (passwordData.password.length < 6) {
      errors.password = 'Şifre en az 6 karakter olmalı';
    }
    
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifre tekrarı gerekli';
    } else if (passwordData.password !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    return errors;
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validatePassword();
    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    try {
      await userService.updatePassword(selectedUserForReset.user_uuid, passwordData.password);
      toast.success('Şifre başarıyla güncellendi');
      setShowPasswordReset(false);
      setSelectedUserForReset(null);
      setPasswordData({ password: '', confirmPassword: '' });
    } catch (error) {
      toast.error('Şifre güncelleme hatası: ' + error.message);
    }
  };

  // Filtrelenmiş ve sıralanmış kullanıcılar
  const filteredAndSortedUsers = useMemo(() => {
    const users = usersData?.users || [];
    let filtered = users || [];
    
    // Filtreleme
    if (filters.name) {
      filtered = filtered.filter(user => 
        user.user_fullname.toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    
    if (filters.department) {
      filtered = filtered.filter(user => 
        user.user_department === filters.department
      );
    }
    
    if (filters.status !== '') {
      filtered = filtered.filter(user => 
        user.user_status === parseInt(filters.status)
      );
    }
    
    // Sıralama
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'user_fullname') {
          aValue = a.user_fullname;
          bValue = b.user_fullname;
        } else if (sortConfig.key === 'user_department') {
          aValue = a.user_department;
          bValue = b.user_department;
        } else if (sortConfig.key === 'user_status') {
          aValue = a.user_status;
          bValue = b.user_status;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [usersData, filters, sortConfig]);

  // Arama
  const handleSearch = (e) => {
    setTempSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(tempSearchTerm);
      setCurrentPage(1);
    }
  };

  // Filtreleme
  const handleFilterKeyDown = (filterType, e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({
        ...prev,
        [filterType]: tempFilters[filterType]
      }));
      setShowFilters(prev => ({
        ...prev,
        [filterType]: false
      }));
    }
  };

  // Select filtreleri için onChange
  const handleSelectFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setShowFilters(prev => ({
      ...prev,
      [filterType]: false
    }));
  };

  const handleTempFilterChange = (filterType, value) => {
    setTempFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setFilters({
      name: '',
      department: '',
      status: ''
    });
    setTempFilters({
      name: '',
      department: '',
      status: ''
    });
    setSearchTerm('');
    setTempSearchTerm('');
    setCurrentPage(1);
  };

  // Aktif filtreleri kontrol et
  const hasActiveFilters = Object.values(filters).some(value => value !== '') || searchTerm !== '';

  // tempFilters'ı filters ile senkronize et
  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  // Export fonksiyonu
  const handleExport = () => {
    const csvContent = [
      ['Ad Soyad', 'Kullanıcı Adı', 'Email', 'Departman', 'Yöneticisi', 'Tip', 'Durum', 'Son Giriş'],
      ...filteredAndSortedUsers.map(user => [
        user.user_fullname,
        user.user_username,
        user.user_email,
        user.user_department,
        user.manager_name || '-',
        user.user_type === 'admin' ? 'Admin' : 
        user.user_type === 'manager' ? 'Manager' : 
        user.user_type === 'user' ? 'User' : 'Guest',
        user.user_status ? 'Aktif' : 'Pasif',
        user.user_last_login ? new Date(user.user_last_login).toLocaleDateString('tr-TR') : '-'
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'kullanicilar.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sayfa başına kayıt sayısı değiştirme
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  if (!['admin', 'manager'].includes(currentUser?.user_type)) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Bu sayfaya erişim yetkiniz yok.</p>
      </div>
    );
  }

  if (isLoading && !usersData) {
    return <LoadingSpinner text="Kullanıcılar yükleniyor..." />;
  }

  const users = usersData?.users || [];
  const pagination = usersData?.pagination || {};

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı ve aksiyonlar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FiUsers size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Kullanıcılar
          </h1>
        </div>
        
        {/* Guest/User kullanıcılar için işlemleri gizle */}
        {currentUser?.user_type !== 'guest' && currentUser?.user_type !== 'user' && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleAddUser}
              className="btn-primary flex items-center"
            >
              <FiPlus className="mr-2" size={16} />
              Yeni Kullanıcı
            </button>
          </div>
        )}
      </div>

      {/* Arama ve Export */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Kullanıcı ara... (Enter tuşuna basın)"
              value={tempSearchTerm}
              onChange={handleSearch}
              onKeyDown={handleSearchKeyDown}
              className="input-field pl-10"
            />
          </div>

          {/* Export butonu */}
          <button 
            onClick={handleExport}
            className="btn-secondary flex items-center justify-center px-4 py-2"
          >
            <TiExportOutline className="mr-2" size={16} />
            Excel
          </button>
        </div>
      </div>

      {/* Filtre Uyarı Mesajı */}
      {hasActiveFilters && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Filtre uygulanmıştır
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {searchTerm && `Arama: "${searchTerm}"`}
                  {filters.name && ` • Ad Soyad: "${filters.name}"`}
                  {filters.department && ` • Departman: "${filters.department}"`}
                  {filters.status && ` • Durum: "${filters.status === '1' ? 'Aktif' : 'Pasif'}"`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiX className="mr-1" size={14} />
                Tüm Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı listesi - Mobil 2 sütun grid (sm altında) */}
      <div className="card p-4 sm:p-6 sm:hidden">
        {/* Açıklama: Mobilde tablo yerine 2 sütun kart düzeni gösterilir */}
        <div className="grid grid-cols-2 gap-3">
          {filteredAndSortedUsers.length > 0 ? (
            filteredAndSortedUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => handleRowClick(user)}
                className="cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Kullanıcı</div>
                  <span className={`${user.user_status ? 'status-active' : 'status-inactive'} text-[10px] px-2 py-0.5`}>{user.user_status ? 'Aktif' : 'Pasif'}</span>
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-1">{user.user_fullname}</div>
                <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400 truncate">{user.user_email}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate mr-2">{user.user_department || '-'}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    user.user_type === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                    user.user_type === 'manager' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    user.user_type === 'user' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}>
                    {user.user_type === 'admin' ? 'Admin' : user.user_type === 'manager' ? 'Manager' : user.user_type === 'user' ? 'User' : 'Guest'}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                  {user.user_last_login ? new Date(user.user_last_login).toLocaleDateString('tr-TR') : '-'}
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Kullanıcı bulunamadı</div>
          )}
        </div>
        {/* Mobil sayfalama: Önceki/Sonraki ve mevcut sayfa bilgisi */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(Math.max(1, Number(currentPage) - 1))}
                disabled={Number(currentPage) <= 1}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) <= 1
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Önceki
              </button>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Sayfa {pagination.currentPage || 1} / {pagination.totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(Number(pagination.totalPages) || 1, Number(currentPage) + 1))}
                disabled={Number(currentPage) >= Number(pagination.totalPages)}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) >= Number(pagination.totalPages)
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Kullanıcı tablosu - Masaüstü (sm ve üzeri) */}
      <div className="card hidden sm:block">
        <div className="table-container">
          <table className="table">
              <thead>
                <tr>
                  <th className="relative">
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => handleSort('user_status')}
                        className="text-left hover:text-blue-600 cursor-pointer"
                      >
                        DURUM
                      </button>
                      <button 
                        onClick={() => toggleFilter('status')}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <IoFilterCircleOutline size={16} />
                      </button>
                    </div>
                    {showFilters.status && (
                      <div className="filter-menu absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                        <select
                          value={tempFilters.status}
                          onChange={(e) => handleSelectFilterChange('status', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        >
                          <option value="">Tümü</option>
                          <option value="1">Aktif</option>
                          <option value="0">Pasif</option>
                        </select>
                      </div>
                    )}
                  </th>
                  <th className="relative">
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => handleSort('user_fullname')}
                        className="text-left hover:text-blue-600 cursor-pointer"
                      >
                        AD SOYAD
                      </button>
                      <button 
                        onClick={() => toggleFilter('name')}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <IoFilterCircleOutline size={16} />
                      </button>
                    </div>
                    {showFilters.name && (
                      <div className="filter-menu absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                        <input
                          type="text"
                          placeholder="Filtrele... (Enter tuşuna basın)"
                          value={tempFilters.name}
                          onChange={(e) => handleTempFilterChange('name', e.target.value)}
                          onKeyDown={(e) => handleFilterKeyDown('name', e)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        />
                      </div>
                    )}
                  </th>
                  <th>Kullanıcı Adı</th>
                  <th>E-POSTA</th>
                  <th className="relative">
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => handleSort('user_department')}
                        className="text-left hover:text-blue-600 cursor-pointer"
                      >
                        DEPARTMAN
                      </button>
                      <button 
                        onClick={() => toggleFilter('department')}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <IoFilterCircleOutline size={16} />
                      </button>
                    </div>
                    {showFilters.department && (
                      <div className="filter-menu absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                        <select
                          value={tempFilters.department}
                          onChange={(e) => handleSelectFilterChange('department', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                        >
                          <option value="">Tümü</option>
                          <option value="Satış">Satış</option>
                          <option value="Pazarlama">Pazarlama</option>
                          <option value="İnsan Kaynakları">İnsan Kaynakları</option>
                          <option value="Muhasebe">Muhasebe</option>
                          <option value="IT">IT</option>
                          <option value="Yönetim">Yönetim</option>
                        </select>
                      </div>
                    )}
                  </th>
                  <th>YÖNETİCİSİ</th>
                  <th>KULLANICI TİPİ</th>
                  <th>Son Giriş</th>
                  <th className="text-center w-20"></th>
                </tr>
              </thead>
            <tbody>
              {filteredAndSortedUsers.length > 0 ? (
                filteredAndSortedUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    onClick={() => handleRowClick(user)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td>
                      <span className={user.user_status ? 'status-active' : 'status-inactive'}>
                        {user.user_status ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.user_fullname}
                        </div>
                       
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {user.user_username}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {user.user_email}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {user.user_department}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {user.manager_name || '-'}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.user_type === 'admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                        user.user_type === 'manager' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                        user.user_type === 'user' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {user.user_type === 'admin' ? 'Admin' :
                         user.user_type === 'manager' ? 'Manager' :
                         user.user_type === 'user' ? 'User' : 'Guest'}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {user.user_last_login ? new Date(user.user_last_login).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td 
                      className="text-center w-20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="relative action-menu">
                        {/* Action menü kuralları: Guest/User gizle + Admin Manager gizle */}
                        {currentUser?.user_type !== 'guest' && 
                         currentUser?.user_type !== 'user' && 
                         !(currentUser?.user_type === 'admin' && user.user_type === 'manager') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleActionMenu(user.id, e);
                            }}
                            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 p-2 -m-2"
                            title="İşlemler"
                          >
                            <CgMenuGridO size={18} />
                          </button>
                        )}
                        
                        {showActionMenu === user.id && (
                          <div className="action-menu-dropdown fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-[9999] min-w-48">
                            <div className="py-1">
                              {/* Admin Manager düzenleme kuralı */}
                              {!(currentUser?.user_type === 'admin' && user.user_type === 'manager') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditUser(user);
                                    setShowActionMenu(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <FiEdit className="mr-3" size={16} />
                                  Düzenle
                                </button>
                              )}
                              {/* Admin Manager şifre resetleme kuralı */}
                              {!(currentUser?.user_type === 'admin' && user.user_type === 'manager') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePasswordReset(user);
                                    setShowActionMenu(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <FiKey className="mr-3" size={16} />
                                  Şifre Sıfırla
                                </button>
                              )}
                              {/* Silme kuralları: Kendi kullanıcıyı silme + Admin Manager silme + Admin Admin silme */}
                              {user.id !== currentUser?.id && 
                               !(currentUser?.user_type === 'admin' && user.user_type === 'manager') &&
                               !(currentUser?.user_type === 'admin' && user.user_type === 'admin') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteUser(user);
                                    setShowActionMenu(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <FiTrash2 className="mr-3" size={16} />
                                  Sil
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedUserForGoal(user);
                                  setShowGoalModal(true);
                                  setShowActionMenu(null);
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                + Hedef Ekle
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Kullanıcı bulunamadı
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">records</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                {pagination.totalItems || 0} kayıt bulundu
              </div>
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sayfa {pagination.currentPage || 1} / {pagination.totalPages || 1}
                </span>
                <div className="flex space-x-1">
                  {(() => {
                    const total = Number(pagination.totalPages) || 1;
                    const current = Number(currentPage) || 1;
                    const pagesSet = new Set([1, total]);
                    for (let p = current - 1; p <= current + 1; p++) {
                      if (p > 1 && p < total) pagesSet.add(p);
                    }
                    const pages = Array.from(pagesSet).sort((a, b) => a - b);

                    const nodes = [];
                    for (let i = 0; i < pages.length; i++) {
                      const p = pages[i];
                      const prev = pages[i - 1];
                      if (i > 0 && p - prev > 1) {
                        nodes.push(
                          <span key={`ellipsis-${prev}-${p}`} className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                        );
                      }
                      nodes.push(
                        <button
                          key={`page-${p}`}
                          onClick={() => handlePageChange(p)}
                          className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                            currentPage === p
                              ? 'bg-brand-primary text-white shadow-lg'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    }
                    return nodes;
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kullanıcı ekleme/düzenleme modal'ı */}
      {showModal && (
        <UserModal
          user={editingUser}
          onClose={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingUser(null);
            queryClient.invalidateQueries('users');
          }}
        />
      )}

        {/* Silme onay modal'ı */}
        {showDeleteModal && selectedUser && (
          <DeleteConfirmModal
            title="Kullanıcıyı Sil"
            message={`"${selectedUser.user_fullname}" kullanıcısını silmek istediğinizden emin misiniz?`}
            onConfirm={confirmDelete}
            onCancel={() => {
              setShowDeleteModal(false);
              setSelectedUser(null);
            }}
            isLoading={deleteUserMutation.isLoading}
          />
        )}

        {/* Şifre sıfırlama modal'ı */}
        {showPasswordReset && selectedUserForReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md mx-4 overflow-hidden">
              {/* Modal başlığı */}
              <div className="bg-brand-primary text-white px-6 py-4">
                <h3 className="text-lg font-semibold">
                  Şifre Sıfırla
                </h3>
                <p className="text-sm text-gray-200 mt-1">
                  {selectedUserForReset.user_fullname} kullanıcısının şifresini güncelleyin
                </p>
              </div>
              
              {/* Modal içeriği */}
              <form onSubmit={handlePasswordResetSubmit} className="p-6">
                <div className="space-y-4">
                  {/* Yeni şifre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Yeni Şifre
                    </label>
                    <input
                      type="password"
                      value={passwordData.password}
                      onChange={(e) => handlePasswordChange('password', e.target.value)}
                      className={`input-field ${passwordErrors.password ? 'input-error' : ''}`}
                      placeholder="Yeni şifre girin"
                    />
                    {passwordErrors.password && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.password}</p>
                    )}
                  </div>

                  {/* Şifre tekrar */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Şifre Tekrar
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      className={`input-field ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
                      placeholder="Şifreyi tekrar girin"
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                {/* Modal butonları */}
                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                  >
                    Şifreyi Güncelle
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordReset(false);
                      setSelectedUserForReset(null);
                    }}
                    className="btn-secondary flex-1"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* Hedef modal'ı */}
      {showGoalModal && selectedUserForGoal && (
        <UserGoalModal
          user={selectedUserForGoal}
          onClose={() => {
            setShowGoalModal(false);
            setSelectedUserForGoal(null);
          }}
        />
      )}
      </div>
    );
  };

  export default Users;
